import type { IDimensions } from '../types';

import { requestMutation } from '../lib/fasterdom/fasterdom';
import { IS_IOS } from './browser/windowEnvironment';
import { throttle } from './schedulers';

const WINDOW_ORIENTATION_CHANGE_THROTTLE_MS = 100;
const WINDOW_RESIZE_THROTTLE_MS = 50; // Faster throttle so keyboard slide is tracked in real-time

let measuringDiv: HTMLDivElement | undefined;

let initialHeight = window.innerHeight;
let maxVisualViewportHeight = window.visualViewport ? window.visualViewport.height : window.innerHeight;
let currentWindowSize = updateSizes();

const handleResize = throttle(() => {
  currentWindowSize = updateSizes();
}, 0, true);

const handleOrientationChange = throttle(() => {
  initialHeight = window.innerHeight;
  if (window.visualViewport) {
    maxVisualViewportHeight = window.visualViewport.height;
  }
  handleResize();
}, WINDOW_ORIENTATION_CHANGE_THROTTLE_MS, false);

window.addEventListener('orientationchange', handleOrientationChange);

// `visualViewport` resize is the real-time signal for keyboard slide on both iOS and Android.
// It fires as the keyboard animates, unlike `window.resize` which fires after.
if (window.visualViewport) {
  window.visualViewport.addEventListener('resize', handleResize);
  window.visualViewport.addEventListener('scroll', handleResize);
}

// Fallback for Android browsers without full visualViewport support
if (!IS_IOS) {
  window.addEventListener('resize', handleResize);
}

if (IS_IOS) {
  // Removed `focusout` and `focusin` polling loops for experimental testing.
}

// Initial load poll to allow layout to fully settle
let initTicks = 0;
const initInterval = setInterval(() => {
  handleResize();
  initTicks++;
  if (initTicks > 10) clearInterval(initInterval);
}, 50);

function getCssTrueHeight(): number {
  if (!measuringDiv) {
    measuringDiv = document.createElement('div');
    measuringDiv.style.position = 'absolute';
    measuringDiv.style.top = '0';
    measuringDiv.style.left = '0';
    measuringDiv.style.width = '1px';
    measuringDiv.style.height = '100vh'; // Fallback
    measuringDiv.style.height = '100dvh'; // Modern dynamic viewport
    measuringDiv.style.visibility = 'hidden';
    measuringDiv.style.pointerEvents = 'none';
    measuringDiv.style.zIndex = '-1';
    document.body.appendChild(measuringDiv);
  }
  return measuringDiv.clientHeight;
}

export function updateSizes(forceClose = false): IDimensions {
  let height: number;

  if (IS_IOS) {
    let vvHeight = window.visualViewport!.height;
    if (vvHeight > maxVisualViewportHeight) {
      maxVisualViewportHeight = vvHeight;
    }
    const isPwa = (
      window.matchMedia('(display-mode: standalone)').matches
      || window.matchMedia('(display-mode: fullscreen)').matches
      || (window.navigator as any).standalone
    );

    const active = document.activeElement as HTMLElement | null;
    const isInput = active && (
      active.tagName === 'INPUT'
      || active.tagName === 'TEXTAREA'
      || active.isContentEditable
      || active.id === 'editable-message-text'
      || active.closest('#editable-message-text')
      || active.closest('[contenteditable]') !== null
    );
    // Auto-heal: when keyboard is explicitly closed via focusout, OR no input is focused,
    // ensure height matches the true screen height
    if (forceClose || (isPwa && !isInput)) {
      const trueScreenHeight = getCssTrueHeight();
      if (isPwa) {
        if (vvHeight < trueScreenHeight && trueScreenHeight > 0) {
          vvHeight = trueScreenHeight;
        } else if (vvHeight < window.screen.height) {
          const isLandscape = window.orientation === 90 || window.orientation === -90;
          const maxPhysical = isLandscape
            ? Math.min(window.screen.width, window.screen.height)
            : Math.max(window.screen.width, window.screen.height);
          if (maxPhysical > vvHeight) vvHeight = maxPhysical;
        }
      } else if (vvHeight < trueScreenHeight && trueScreenHeight > 0) {
        vvHeight = trueScreenHeight;
      }
    }

    height = vvHeight;
  } else {
    // Android strategy: use `visualViewport.height` as the source of truth.
    // It shrinks in real-time as the keyboard slides up, unlike `window.innerHeight`
    // which only updates after the full animation completes (causing the "hidden composer" bug).
    const isAndroidPwa = (
      window.matchMedia('(display-mode: standalone)').matches
      || window.matchMedia('(display-mode: fullscreen)').matches
    );

    if (isAndroidPwa) {
      const isLandscape = window.orientation === 90 || window.orientation === -90;
      const maxPhysical = isLandscape
        ? Math.min(window.screen.width, window.screen.height)
        : Math.max(window.screen.width, window.screen.height);

      if (window.visualViewport) {
        const vvH = window.visualViewport.height;
        if (maxPhysical - vvH > 100) {
          // Keyboard is open: use visualViewport to get the exact visible area
          height = vvH;
        } else {
          // Keyboard is closed: use physical screen height to hide the Android nav bar gap
          height = maxPhysical;
        }
      } else {
        height = window.innerHeight;
      }
    } else {
      height = window.innerHeight;
    }
  }

  requestMutation(() => {
    const vh = height * 0.01;
    document.documentElement.style.setProperty('--vh', `${vh}px`);

    if (IS_IOS) {
      const isPwa = (
        window.matchMedia('(display-mode: standalone)').matches
        || window.matchMedia('(display-mode: fullscreen)').matches
        || (window.navigator as any).standalone
      );
      if (isPwa) {
        const keyboardHeight = Math.max(0, window.innerHeight - height);
        document.documentElement.style.setProperty('--keyboard-height', `${keyboardHeight}px`);
      }
    }

    // Lock html/body height on Android PWAs to prevent overflow clipping at the bottom
    if (!IS_IOS && (window.matchMedia('(display-mode: standalone)').matches || window.matchMedia('(display-mode: fullscreen)').matches)) {
      document.documentElement.style.height = `${height}px`;
      document.body.style.height = `${height}px`;
    }
  });

  return {
    width: window.innerWidth,
    height,
  };
}

const windowSize = {
  get: () => currentWindowSize,
  getIsKeyboardVisible: () => {
    if (IS_IOS && window.visualViewport) {
      const active = document.activeElement as HTMLElement | null;
      const isInput = active && (
        active.tagName === 'INPUT'
        || active.tagName === 'TEXTAREA'
        || active.isContentEditable
        || active.id === 'editable-message-text'
        || active.closest('#editable-message-text')
        || active.closest('[contenteditable]') !== null
      );
      const isViewportShrunk = maxVisualViewportHeight - window.visualViewport.height > 50;
      return Boolean(isInput && isViewportShrunk);
    }
    // Android: keyboard visible if visualViewport is significantly smaller than initial height
    if (!IS_IOS && window.visualViewport) {
      return initialHeight - window.visualViewport.height > 100;
    }
    return initialHeight > currentWindowSize.height;
  },
};

export default windowSize;
